import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { Errors } from "services/forms/validation/types";
import { FleetTireSetEditFormValues } from "services/api/fleet/tires/types";
import { initialFleetTireSetEditForm } from "services/api/fleet/tires/consts";
import {
  getFleetTireSetAPI,
  getFleetTireSetByVehicleActiveAPI,
  mountFleetTireSetAPI,
} from "services/api/fleet/tires/api";
import { transformFleetTireSetEditForm } from "services/api/fleet/tires/transformations";
import { getDimensionSet, getTireType, getTireManufacturers } from "services/api/fleet/tires/helpers";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { getFleetWarehouseAPI } from "services/api/fleet/warehouses/api";

import { Modal, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import successAnimation from "ui/theme/animations/success.json";

import { TireSetMountEditCardProps, MountFormValues } from "./types";
import MountStep1 from "./components/MountStep1";
import MountStep2 from "./components/MountStep2";
import MountSummary from "./components/MountSummary";
import TireSetMountIntro from "./components/TireSetMountIntro/TireSetMountIntro";

interface ResolvedMountNames {
  targetVehicle?: {
    name?: string | null;
    registration?: string | null;
    image?: string | null;
  };
  oldSetOnVehicle?: {
    code?: string | null;
    dimension?: string | null;
    type?: string | null;
    manufacturer?: string | null;
  };
  oldSetWarehouseName?: string | null;
}

const initialMountFormValues: MountFormValues = {
  targetVehicleId: null,
  transferDate: new Date(),
  kmTraveled: null,
  oldSetWarehouseId: null,
};

const TireSetMountEditCard: React.FC<TireSetMountEditCardProps> = (props) => {
  const { show, tireSetId, onSuccess, onClose } = props;
  const { t } = useTranslate();

  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [currentSet, setCurrentSet] = useState<FleetTireSetEditFormValues>(initialFleetTireSetEditForm);
  const [formValues, setFormValues] = useState<MountFormValues>(initialMountFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [resolvedNames, setResolvedNames] = useState<ResolvedMountNames>({});

  useEffect(() => {
    if (show && tireSetId) {
      setStepCurrent(0);
      setFormValues(initialMountFormValues);
      setErrors({});
      fetchTireSet(tireSetId);
    }
  }, [show, tireSetId]);

  const fetchTireSet = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetTireSetAPI(id);
      if (result) {
        const setForm = transformFleetTireSetEditForm(result);
        setCurrentSet(setForm);
      }
    } catch { /* */ }
    setIsLoading(false);
  };

  const handleChange = useCallback((partial: Partial<MountFormValues>) => {
    setFormValues((prev) => ({ ...prev, ...partial }));
    setErrors({});
  }, []);

  const validateStep = (): boolean => {
    const newErrors: Errors = {};

    if (stepCurrent === 0) {
      if (!formValues.targetVehicleId) {
        newErrors.targetVehicleId = "Odaberite vozilo";
      }
      if (!formValues.transferDate) {
        newErrors.transferDate = "Odaberite datum montaže";
      }
      if (formValues.kmTraveled == null) {
        newErrors.kmTraveled = "Unesite trenutnu kilometražu vozila";
      }
    }

    if (stepCurrent === 1) {
      if (!formValues.oldSetWarehouseId) {
        newErrors.oldSetWarehouseId = "Odaberite skladište za stari set";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resolveNamesForSummary = async () => {
    const names: ResolvedMountNames = {};
    try {
      // Resolve target vozilo
      if (formValues.targetVehicleId) {
        const v = await getFleetVehicleAPI(formValues.targetVehicleId);
        names.targetVehicle = {
          name: v.name ?? "-",
          registration: v.registrationPlate ?? null,
          image: v.defaultImage ?? null,
        };
      }

      // Resolve stari set na vozilu
      if (formValues.targetVehicleId) {
        try {
          const active = await getFleetTireSetByVehicleActiveAPI(formValues.targetVehicleId);
          if (active) {
            names.oldSetOnVehicle = {
              code: active.code,
              dimension: getDimensionSet(active.tires),
              type: getTireType(active.tires)?.name ?? null,
              manufacturer: getTireManufacturers(active.tires) || null,
            };
          }
        } catch { /* */ }
      }

      // Resolve skladište za stari set
      if (formValues.oldSetWarehouseId) {
        const w = await getFleetWarehouseAPI(formValues.oldSetWarehouseId);
        names.oldSetWarehouseName = w.name;
      }
    } catch { /* */ }
    setResolvedNames(names);
  };

  const handleNextStep = async () => {
    if (!validateStep()) return;

    const nextStep = stepCurrent + 1;
    const summaryStepIndex = 2;

    // Resolve nazive prije ulaska u summary korak
    if (nextStep === summaryStepIndex) {
      await resolveNamesForSummary();
    }

    setStepCurrent(nextStep);
  };

  const handleBackStep = () => {
    setStepCurrent((s) => s - 1);
  };

  const handleFinish = async () => {
    setIsLoading(true);
    try {
      await mountFleetTireSetAPI(
        tireSetId,
        {
          targetVehicleId: formValues.targetVehicleId!,
          oldSetWarehouseId: formValues.oldSetWarehouseId,
          transferDate: formValues.transferDate,
          kmTraveled: formValues.kmTraveled!,
        },
        t
      );

      setIsLoading(false);
      setShowSuccessOverlay(true);
    } catch {
      setIsLoading(false);
    }
  };

  const handleSuccessDismiss = () => {
    setShowSuccessOverlay(false);
    setFormValues(initialMountFormValues);
    setStepCurrent(0);
    if (onSuccess) onSuccess();
  };

  const steps = useMemo(() => {
    return [
      <WizardStep key={1} stepNumber={0} stepCurrent={stepCurrent}>
        <MountStep1
          formValues={formValues}
          setFormValues={setFormValues}
          errors={errors}
        />
      </WizardStep>,
      <WizardStep key={2} stepNumber={1} stepCurrent={stepCurrent}>
        <MountStep2
          formValues={formValues}
          setFormValues={setFormValues}
          errors={errors}
        />
      </WizardStep>,
      <WizardStep key={3} stepNumber={2} stepCurrent={stepCurrent}>
        <MountSummary
          currentSet={currentSet}
          formValues={formValues}
          resolvedNames={resolvedNames}
        />
      </WizardStep>,
    ];
  }, [stepCurrent, currentSet, formValues, errors, handleChange, resolvedNames]);

  return (
    <>
      <SuccessOverlay
        show={showSuccessOverlay}
        animation={successAnimation}
        title="Montaža je uspješno izvršena!"
        description="Set guma je montiran na vozilo."
        onDismiss={handleSuccessDismiss}
      />
      <Modal
        show={show}
        onHide={onClose ?? (() => {})}
        position="side"
        size="full"
        intro={{
          content: (
            <TireSetMountIntro
              currentSet={currentSet}
              formValues={formValues}
              targetVehicleName={resolvedNames.targetVehicle?.name}
              targetVehicleRegistration={resolvedNames.targetVehicle?.registration}
              targetVehicleImage={resolvedNames.targetVehicle?.image}
              oldSetCode={resolvedNames.oldSetOnVehicle?.code}
              oldSetWarehouseName={resolvedNames.oldSetWarehouseName}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title="Montaža seta guma"
          onHide={onClose ?? (() => {})}
        />
        <ModalBody>
          {isLoading && (
            <div className="relative h-[calc(100vh-80px)]">
              <Loader isLoading={true}>{null}</Loader>
            </div>
          )}
          <div className={isLoading ? "hidden" : ""}>
            <Wizard steps={steps} />
          </div>
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
    </>
  );
};

export default memo(TireSetMountEditCard);
